<script setup lang="ts">
import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome'
import {defineAsyncComponent, ref, onMounted, watch} from 'vue'
import {getSettings, getTranslation, getUserLocation, setLogo} from '@/ts/utilities.ts'

const TopNavigation = defineAsyncComponent(() => import('@/components/TopNavigation.vue'))
const RenderAction = defineAsyncComponent(() => import('@/components/helpers/RenderAction.vue'))
const ActionBar = defineAsyncComponent(() => import('@/components/ActionBar.vue'))
const Navigation = defineAsyncComponent(() => import('@/components/Navigation.vue'))
const SearchBar = defineAsyncComponent(() => import('@/components/SearchBar.vue'))
const ShopWidget = defineAsyncComponent(() => import('@/components/ShopWidget.vue'))
const ShopBar = defineAsyncComponent(() => import('@/components/ShopBar.vue'))

const isCart = ref(false)
const isCheckout = ref(false)
const isCompletedOrder = ref(false)
const toggleNavigation = ref(false)
const settings = getSettings()

const props = defineProps({
	getUserLocation: {
		type: String,
		default: "False"
	}
})

const back = () => {
	window.history.back()
}


onMounted(() => {
	const url = new URL(window.location.href)
	const urlParams = new URLSearchParams(window.location.search)

	if (url.pathname.includes('indkoebskurv')) {
		isCart.value = true
	}

	if (url.pathname.includes('checkout')) {
		isCheckout.value = true
	}

	if (isCheckout && urlParams.has('CompletedOrderId')) {
		isCompletedOrder.value = true
	}

	if (props.getUserLocation == "True") {
		// Ensure we only grab the user location once.
		if (!localStorage.getItem('locationFetched')) {
			getUserLocation()
			localStorage.setItem('locationFetched', 'true');
		}
	}
})

watch(toggleNavigation, (value) => {
	if (value) {
		document.body.classList.add('h-dvh', 'overflow-y-hidden')
	} else {
		document.body.classList.remove('h-dvh', 'overflow-y-hidden')
	}
})


</script>

<template>
	<TopNavigation />


	<header class="relative">
		<div class="container-fluid lg:border-b lg:border-b-grey">
			<div class="container">
				<div class="grid items-center gap-x-2 gap-y-4 xl:gap-8 pt-4 md:py-4 lg:grid-rows-1 grid-cols-4 lg:grid-cols-3 auto-rows-max"
					:class="isCheckout ? 'grid-rows-1' : 'grid-rows-2'">

					<div v-if="isCheckout && !isCompletedOrder" class="w-fit col-span-2 lg:col-span-1">
						<RenderAction
							action="button"
							theme="white"
							@click="back()">
							<font-awesome-icon :icon="['fas', 'arrow-left']" />
							{{ getTranslation('Back') }}
						</RenderAction>
					</div>

					<div class="flex items-center justify-start col-span-3 lg:col-span-1 lg:col-start-1"
						:class="isCheckout ? 'col-start-3' : ''">

						<button class="flex flex-col justify-center items-center w-12 lg:hidden mr-4"
								@click="toggleNavigation = true">
							<font-awesome-icon :icon="['fal', 'bars']" class="text-3xl" />
							<span class="mt-1 text-xs md:text-sm">{{ getTranslation('MobileMenu.Text') }}</span>
						</button>

							<a href="/" class="flex flex-col items-start">
								<img
									width="400"
									height="46"
									class="max-h-[40px] hidden md:inline-block object-contain object-left"
									:src="setLogo().logoPath"
									:alt="getTranslation('LogoAltText')"
									loading="lazy" />
								<img
									width="400"
									height="46"
									class="max-h-[40px] inline md:hidden object-contain object-left"
									:src="setLogo().mobileLogoPath"
									:alt="getTranslation('LogoAltText')"
									loading="lazy" />
								<small v-if="setLogo().serviceText"
									   class="text-xs text-grey-dark text-left">
									{{ setLogo().serviceText }}
								</small>
							</a>

					</div>

					<ActionBar v-if="!isCheckout" class="flex items-center col-span-1 lg:col-span-1 lg:col-start-3" />

					<div v-if="!isCheckout" class="flex col-span-full gap-x-2 lg:col-span-1 lg:col-start-2 lg:row-start-1">
						<SearchBar id="js-search-bar"
								   class="w-full"
								   data-no-results="@SearchNoResults"/>
					</div>

					<div v-if="isCheckout" class="flex items-center justify-end col-span-2 lg:col-span-1">
						<!-- TODO: Check that it displays data -->
						<ShopWidget></ShopWidget>
					</div>
				</div>
			</div>

		</div>
		<div class="container-fluid border-b border-b-grey pb-2 mb-2 lg:pb-0" v-if="!isCheckout">
			<div class="container" >
				<div class="pt-2 md:py-2">
					<ShopBar />
				</div>
			</div>
		</div>



		<div v-if="!isCheckout" class="container lg:py-4">
			<Navigation
				:toggleNavigation="toggleNavigation"
				@closeMobileMenu="toggleNavigation = false"
			/>
		</div>
	</header>
</template>

<style scoped>

</style>
